import React from "react";
import {
  Body3MediumTypography,
  Body5MediumTypography,
  Body5RegularTypography,
  Body6RegularTypography,
  Caps2SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import { Icon } from "../../../components/Icon/Icon";
import { colorPalette } from "../../../theme/colors";
import { OrderCardProps } from "./OrderCard.types";
import { ProductImage, StyledLink } from "./OrderCard.styles";
import Box from "../../../primitives/Box/Box";
import { ActionButton } from "../../../components/ActionButton/ActionButton";
import { getAssetUrl } from "../../../utils/constants";
import { VerticalSpacer } from "../../../primitives/Spacer/VerticalSpacer";
import Tags from "../../../components/Tags/Tags";

export const OrderCard: React.FC<OrderCardProps> = ({
  statusBadge,
  contentLiveStatus,
  product,
  onActionClick,
  actionIcon,
  actionIconColor,
  actionBackgroundColor,
  contentLiveDaysRemaining,
  statusDetails,
  statusDetailsActionButton,
  cardBackgroundColor = colorPalette.popWhite[500],
  cardBorderColor = colorPalette.popWhite[100],
  isOutOfStock = false,
  isBarter = false,
}) => {
  return (
    <FlexColumn
      backgroundColor={cardBackgroundColor}
      borderColor={cardBorderColor || "border.default.default"}
      border="1px solid"
      borderRadius="8px"
      px={8}
      py={4}
      gap={8}
    >
      {/* Header: Status Badge */}
      <FlexRow justifyContent="space-between">
        {statusBadge && (
          <Tags
            variant="primary"
            label={statusBadge.text}
            backgroundColor={
              statusBadge.backgroundColor || "background.default.default"
            }
            color={statusBadge.textColor || "text.default.default"}
            icon={statusBadge.icon || "fa-solid fa-circle-info"}
            iconColor={statusBadge.textColor || "text.default.default"}
          />
        )}
        {contentLiveStatus?.show && (
          <Tags
            variant="primary"
            icon={
              contentLiveStatus.contentExists
                ? "fa-solid fa-check-circle"
                : "fa-solid fa-clock"
            }
            iconColor={
              contentLiveStatus.contentExists
                ? "text.positive.tertiary"
                : "text.default.default"
            }
            label={
              contentLiveStatus.contentExists
                ? "Content Live"
                : "Content Pending"
            }
            color={"text.default.default"}
            backgroundColor={
              contentLiveStatus.contentExists
                ? "text.wishlink.on-wishlink"
                : "background.warning.tertiary"
            }
          />
        )}
      </FlexRow>

      {/* Product Section */}
      <FlexColumn position="relative" gap={8}>
        <FlexRow alignItems="flex-start" gap={8} width="100%">
          <ProductImage
            width="60px"
            height="60px"
            borderRadius="5px"
            objectFit="cover"
            src={product.imageUrl}
            placeholder={getAssetUrl("default-product-thumb.png")}
            alt={product.productTitle}
            grayscale={isOutOfStock}
          />
          <FlexColumn gap={4} justifyContent="space-between">
            <Body3MediumTypography
              color="text.default.default"
              lineClamp={1}
              textOverflow="ellipsis"
              fontFamily="Lexend"
            >
              {product.brandName}
            </Body3MediumTypography>
            <Body5RegularTypography
              color="text.neutral.secondary"
              lineClamp={1}
              textOverflow="ellipsis"
              fontFamily="Lexend"
            >
              {product.productTitle}
            </Body5RegularTypography>

            {product.variantDetails && (
              <Caps2SemiboldTypography
                color="text.neutral.secondary"
                lineClamp={1}
                textOverflow="ellipsis"
              >
                Details: {product.variantDetails}
              </Caps2SemiboldTypography>
            )}
            {isBarter && (
              <Box>
                <VerticalSpacer size={4} />
                <Tags
                  variant="secondary"
                  label="On Barter"
                  backgroundColor="background.default.secondary"
                  color="text.default.secondary"
                />
              </Box>
            )}
          </FlexColumn>
          <FlexColumn
            onClick={onActionClick}
            width="24px"
            height="24px"
            borderRadius="4px"
            justifyContent="center"
            alignItems="center"
            p={4}
            backgroundColor={
              actionBackgroundColor || "background.wishlink.tertiary"
            }
          >
            <Icon
              name={actionIcon || "fa-solid fa-copy"}
              size="12"
              width="16"
              height="16"
              color={actionIconColor || "icon.wishlink.default"}
            />
          </FlexColumn>
        </FlexRow>
        {statusDetails && statusDetails.length > 0 && (
          <>
            <Box height="1px" width="100%" backgroundColor={cardBorderColor} />
            <FlexRow justifyContent="space-between">
              <FlexColumn gap={4}>
                {statusDetails.map((row, idx) => (
                  <FlexRow key={idx}>
                    {row.isLink ? (
                      <Body6RegularTypography
                        color={row.textColor || colorPalette.popBlack[500]}
                      >
                        Track order here:{" "}
                        <StyledLink
                          color={colorPalette.info[500]}
                          href={row.value}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {row.value}
                        </StyledLink>
                      </Body6RegularTypography>
                    ) : row.label === "NA" ? (
                      <Body6RegularTypography
                        color={row.textColor || colorPalette.popBlack[500]}
                      >
                        {row.value}
                      </Body6RegularTypography>
                    ) : (
                      <Body6RegularTypography
                        color={row.textColor || colorPalette.popBlack[500]}
                      >
                        {row.label}: {row.value || "Track order here"}
                      </Body6RegularTypography>
                    )}
                  </FlexRow>
                ))}
              </FlexColumn>
              {statusDetailsActionButton && (
                <FlexRow justifyContent="flex-end" width="fit-content">
                  <ActionButton
                    variant={statusDetailsActionButton.variant || "primary"}
                    onClick={statusDetailsActionButton.onClick}
                    disabled={statusDetailsActionButton.disabled}
                  >
                    {statusDetailsActionButton.text}
                  </ActionButton>
                </FlexRow>
              )}
            </FlexRow>
          </>
        )}

        {/* Content Live Days Remaining Nudge */}
        {contentLiveDaysRemaining?.showContentStatus &&
          !contentLiveDaysRemaining?.contentExists && (
            <FlexRow
              p={8}
              borderRadius="8px"
              backgroundColor="background.utilities.info-primary"
            >
              <Body6RegularTypography color="text.utilities.text-on-info-primary">
                {contentLiveDaysRemaining.daysLeft <= 0
                  ? "Please post your content and publish it on Wishlink Shop"
                  : `${contentLiveDaysRemaining.daysLeft} days left for creating content on this product`}
              </Body6RegularTypography>
            </FlexRow>
          )}
      </FlexColumn>
    </FlexColumn>
  );
};
